import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { WatchlistFacade, WatchlistPageContext } from '@planone/bau365/data-access-user';
import { EntityType } from '@planone/bau365/util-fair-data';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { BehaviorSubject, combineLatest, Observable, startWith, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'bau365-toggle-watchlist-item',
  templateUrl: './toggle-watchlist-item.component.html',
  styleUrls: ['./toggle-watchlist-item.component.scss'],
})
export class ToggleWatchlistItemComponent implements OnInit, OnDestroy {
  @Input()
  set entityId(entityId: string | number | undefined) {
    this.entityId$.next(entityId?.toString());
  }

  @Input()
  set entityType(entityType: 'PRODUCT' | 'EXHIBITOR' | 'POST' | 'EVENT' | 'BRAND') {
    this.entityType$.next(entityType as EntityType);
  }

  @Input()
  set iconOnly(iconOnly: boolean | undefined) {
    this.iconOnly$.next(iconOnly ?? true);
  }

  @Input()
  set pageContext(pageContext: WatchlistPageContext | undefined) {
    this.pageContext$.next(pageContext);
  }

  @Input()
  set fairId(fairId: string | undefined) {
    this.fairId$.next(fairId);
  }

  itemSelected$: Observable<boolean>;
  buttonText$: Observable<string>;

  buttonMode$ = new BehaviorSubject<'iconOnly' | 'fullSelected' | 'fullUnselected'>('iconOnly');

  watchlistEnabled$: Observable<boolean | undefined>;

  private entityId$ = new BehaviorSubject<string | undefined>(undefined);
  private entityType$ = new BehaviorSubject<EntityType | undefined>(undefined);
  private iconOnly$ = new BehaviorSubject<boolean>(true);
  private pageContext$ = new BehaviorSubject<WatchlistPageContext | undefined>(undefined);
  private fairId$ = new BehaviorSubject<string | undefined>(undefined);
  private toggleItem$ = new Subject<void>();
  private unsubscribe$ = new Subject<void>();

  constructor(private watchlistFacade: WatchlistFacade, private features: DataAccessTogglesFacade) {
    this.itemSelected$ = combineLatest([
      this.watchlistFacade.allWatchlistItems$,
      this.entityType$,
      this.entityId$,
    ]).pipe(
      map(([watchlistItems, entityType, entityId]) => {
        if (entityType === undefined || entityId === undefined) {
          return false;
        }
        return (
          watchlistItems?.find((item) => item.entity_id === entityId && item.entity_type === entityType) !== undefined
        );
      })
    );

    this.buttonText$ = combineLatest([this.itemSelected$, this.entityType$]).pipe(
      map(([selected, type]) => {
        if (selected) {
          return $localize`:@@featureWatchlist-toggleWatchlistItem-unselect:Nicht mehr merken`;
        } else {
          switch (type) {
            case EntityType.event:
              return $localize`:@@featureWatchlist-toggleWatchlistItem-selectEvent:Event merken`;
            case EntityType.post:
              return $localize`:@@featureWatchlist-toggleWatchlistItem-selectPost:Beitrag merken`;
            case EntityType.exhibitor:
              return $localize`:@@featureWatchlist-toggleWatchlistItem-selectExhibitor:Aussteller merken`;
            case EntityType.product:
              return $localize`:@@featureWatchlist-toggleWatchlistItem-selectProduct:Produkt merken`;
            default:
              return $localize`:@@featureWatchlist-toggleWatchlistItem-selectDefault:Merken`;
          }
        }
      })
    );
    this.watchlistEnabled$ = this.features.flag('kill-watchlist').pipe(map((watchlistDisabled) => !watchlistDisabled));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit() {
    this.toggleItem$
      .pipe(
        withLatestFrom(this.itemSelected$, this.entityId$, this.entityType$, this.pageContext$, this.fairId$),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([_, selected, entity_id, entity_type, pageContext, fairId]) => {
        if (!pageContext) {
          pageContext = 'home';
        }

        if (!!entity_id && !!entity_type && !!pageContext && !!fairId) {
          const entity = { entity_id, entity_type, fair_id: fairId };

          if (selected) {
            this.watchlistFacade.removeWatchlistItem(entity, pageContext);
          } else {
            this.watchlistFacade.addWatchlistItem(entity, pageContext);
          }
        }
      });

    combineLatest([this.itemSelected$.pipe(startWith(false)), this.iconOnly$])
      .pipe(
        map(([selected, iconOnly]) => {
          if (iconOnly) {
            return 'iconOnly';
          } else {
            return selected ? 'fullSelected' : 'fullUnselected';
          }
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((buttonMode) => {
        this.buttonMode$.next(buttonMode);
      });
  }

  onToggleItem(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.toggleItem$.next();
  }
}
